/**
 * Reusable UI Components Library
 * Consistent components used across all pages
 */

import React from "react";
import { motion } from "framer-motion";
import { Search, X, ChevronDown } from "lucide-react";

/**
 * SearchFilter Component - Search and Filter bar for list pages
 */
export const SearchFilter = ({
  searchTerm,
  onSearchChange,
  filterOptions,
  selectedFilter,
  onFilterChange,
  placeholder = "Search...",
  className = "",
}) => {
  return (
    <div className={`flex flex-col md:flex-row gap-3 mb-6 ${className}`}>
      {/* Search Input */}
      <div className="flex-1 relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <Search size={18} />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          className="
            w-full pl-10 pr-4 py-2.5
            border border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500
            text-sm text-gray-900 placeholder-gray-500
            transition-all duration-200
          "
        />
      </div>

      {/* Filter Dropdown */}
      {filterOptions && filterOptions.length > 0 && (
        <div className="relative min-w-[200px]">
          <select
            value={selectedFilter}
            onChange={(e) => onFilterChange(e.target.value)}
            className="
              w-full px-4 py-2.5
              border border-gray-300 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500
              bg-white text-sm text-gray-900
              transition-all duration-200
              appearance-none cursor-pointer
              pr-10
            "
          >
            <option value="">All Items</option>
            {filterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>
      )}
    </div>
  );
};

/**
 * ActionButton Component - Consistent action buttons
 */
export const ActionButton = ({
  icon: Icon,
  label,
  onClick,
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  className = "",
}) => {
  const variants = {
    primary:
      "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-400/50",
    danger: "bg-red-600 hover:bg-red-700 text-white focus:ring-red-400/50",
    success: "bg-green-600 hover:bg-green-700 text-white focus:ring-green-400/50",
    warning:
      "bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-400/50",
    ghost:
      "bg-transparent hover:bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300",
    secondary: "bg-gray-200 hover:bg-gray-300 text-gray-800",
  };

  const sizes = {
    sm: "px-2 py-1 text-xs gap-1",
    md: "px-3 py-1.5 text-sm gap-2",
    lg: "px-4 py-2 text-base gap-2",
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        flex items-center justify-center
        rounded-lg font-medium
        transition-all duration-200
        active:scale-95 focus:outline-none focus:ring-2
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-current
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
    >
      {Icon && <Icon size={16} />}
      {label && <span>{label}</span>}
      {loading && <span className="animate-spin">⌛</span>}
    </motion.button>
  );
};

/**
 * ListCard Component - Consistent list item card
 */
export const ListCard = ({
  avatar,
  title,
  subtitle,
  status,
  actions,
  onClick,
  className = "",
}) => {
  return (
    <motion.div
      className={`
        bg-white border border-gray-200 rounded-lg
        hover:shadow-md hover:border-gray-300
        transition-all duration-200
        p-4 flex items-center gap-4
        cursor-pointer
        ${className}
      `}
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ y: 0 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Avatar */}
      {avatar && (
        <div
          className="
            w-12 h-12 rounded-full
            bg-gradient-to-br from-blue-400 to-blue-600
            flex items-center justify-center text-white font-bold text-sm
            flex-shrink-0
          "
        >
          {avatar}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 truncate">{subtitle}</p>}
        {status && (
          <span
            className={`
            inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium
            ${
              status.type === "active"
                ? "bg-green-100 text-green-800"
                : status.type === "inactive"
                ? "bg-gray-100 text-gray-800"
                : status.type === "warning"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
            }
          `}
          >
            {status.label}
          </span>
        )}
      </div>

      {/* Actions */}
      {actions && (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </motion.div>
  );
};

/**
 * StatusBadge Component - Status indicator badge
 */
export const StatusBadge = ({ status, label }) => {
  const styles = {
    active: "bg-green-100 text-green-800",
    inactive: "bg-gray-100 text-gray-800",
    pending: "bg-yellow-100 text-yellow-800",
    error: "bg-red-100 text-red-800",
    info: "bg-blue-100 text-blue-800",
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {label}
    </span>
  );
};

/**
 * EmptyState Component - Show when no results
 */
export const EmptyState = ({
  icon: Icon,
  title = "No results found",
  description = "Try adjusting your search or filter criteria",
  action,
}) => {
  return (
    <motion.div
      className="
        flex flex-col items-center justify-center py-12
        text-center
      "
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {Icon && (
        <div className="mb-4 text-gray-400">
          <Icon size={48} />
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-sm">{description}</p>
      {action && action}
    </motion.div>
  );
};

/**
 * LoadingState Component - Show loading skeleton
 */
export const LoadingState = ({ count = 5 }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          className="
            h-16 bg-gray-200 rounded-lg
            animate-pulse
          "
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />
      ))}
    </div>
  );
};

/**
 * ScrollableList Component - List with custom scrollbar
 */
export const ScrollableList = ({ children, className = "", maxHeight = "600px" }) => {
  return (
    <div
      className={`
        custom-scrollbar
        overflow-y-auto rounded-lg
        bg-white border border-gray-200
        ${className}
      `}
      style={{
        maxHeight,
        scrollbarWidth: "thin",
        scrollbarColor: "#cbd5e1 #f1f5f9",
      }}
    >
      {children}
    </div>
  );
};

/**
 * ConfirmDialog Component - Delete/action confirmation
 */
export const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  isDangerous = false,
}) => {
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-gray-200"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600">{message}</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="
              px-4 py-2 text-sm font-medium
              bg-gray-200 hover:bg-gray-300 text-gray-800
              rounded-lg transition-colors duration-200
            "
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`
              px-4 py-2 text-sm font-medium text-white rounded-lg
              transition-colors duration-200
              ${
                isDangerous
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-blue-600 hover:bg-blue-700"
              }
            `}
          >
            {confirmText}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * Toast Component - Notification toast
 */
export const Toast = ({ type = "info", message, icon: Icon }) => {
  const styles = {
    success: "bg-green-50 border-green-300 text-green-700",
    error: "bg-red-50 border-red-300 text-red-700",
    warning: "bg-yellow-50 border-yellow-300 text-yellow-700",
    info: "bg-blue-50 border-blue-300 text-blue-700",
  };

  return (
    <motion.div
      className={`
        flex items-center gap-3 p-4 rounded-lg border
        ${styles[type]}
      `}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      {Icon && <Icon size={20} />}
      <span className="text-sm font-medium">{message}</span>
    </motion.div>
  );
};

/**
 * PaginationOh wait, let me create the pagination component separately as it might be needed
 */
export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) => {
  return (
    <div className={`flex items-center justify-between mt-6 ${className}`}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="
          px-3 py-1.5 text-sm font-medium
          bg-gray-200 hover:bg-gray-300 text-gray-800
          rounded-lg transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
        "
      >
        Previous
      </button>

      <div className="flex items-center gap-1">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i + 1}
            onClick={() => onPageChange(i + 1)}
            className={`
              w-8 h-8 rounded-lg font-medium text-sm
              transition-all duration-200
              ${
                currentPage === i + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-900 hover:bg-gray-200"
              }
            `}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="
          px-3 py-1.5 text-sm font-medium
          bg-gray-200 hover:bg-gray-300 text-gray-800
          rounded-lg transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
        "
      >
        Next
      </button>
    </div>
  );
};
